import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Edit, Trash2, Upload, Image, Loader2,
  FolderPlus, ImagePlus, RefreshCw, Crop
} from 'lucide-react';
import API_ENDPOINTS, { getImageUrl } from '../../config/api';
import ImageCropModal from './ImageCropModal';
import AdminPhotoMasonry from './AdminPhotoMasonry';
import { focalPointFromCrop } from '../../utils/heroCrop';
import {
  isAllowedImageFile,
  maxImageUploadLabel,
  MAX_IMAGE_UPLOAD_BYTES,
} from '../../utils/imageUpload';

const GalleryManagement = () => {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showAlbumForm, setShowAlbumForm] = useState(false);
  const [showPhotoForm, setShowPhotoForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [albumForm, setAlbumForm] = useState({ name: '', description: '', cover_image: '' });
  const [photoForm, setPhotoForm] = useState({ album_id: '', image_url: '', caption: '' });
  const [refreshing, setRefreshing] = useState(false);
  const [cropPreviewUrl, setCropPreviewUrl] = useState(null);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [editingHeroPhoto, setEditingHeroPhoto] = useState(null);
  const fileInputRef = useRef(null);
  const pendingAlbumIdRef = useRef(null);

  useEffect(() => { fetchAlbums(); }, []);

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.ADMIN_GALLERY}?type=albums`);
      if (response.data.success) {
        const albumsWithPhotos = [];
        for (const album of response.data.albums) {
          const photosResponse = await axios.get(`${API_ENDPOINTS.ADMIN_GALLERY}?album_id=${album.id}`);
          albumsWithPhotos.push({
            ...album,
            photos: photosResponse.data.success ? photosResponse.data.photos : []
          });
        }
        setAlbums(albumsWithPhotos);
        if (albumsWithPhotos.length > 0 && !selectedAlbum) {
          setSelectedAlbum(albumsWithPhotos[0]);
        } else if (selectedAlbum) {
          // Update selected album with new photos
          const updatedAlbum = albumsWithPhotos.find(a => a.id === selectedAlbum.id);
          if (updatedAlbum) setSelectedAlbum(updatedAlbum);
        }
      }
    } catch (error) { 
      console.error('Error fetching albums:', error);
    } finally { 
      setLoading(false); 
    }
  };

  const getImageDimensions = (src) =>
    new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = src;
    });

  const closeCropModal = () => {
    if (cropPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(cropPreviewUrl);
    setCropPreviewUrl(null);
    setPendingUploadFile(null);
    setEditingHeroPhoto(null);
    pendingAlbumIdRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadImageWithCrop = async (file, croppedAreaPixels) => {
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);
    uploadFormData.append('type', 'gallery');
    uploadFormData.append(
      'crop_data',
      JSON.stringify({
        x: Math.round(croppedAreaPixels.x),
        y: Math.round(croppedAreaPixels.y),
        width: Math.round(croppedAreaPixels.width),
        height: Math.round(croppedAreaPixels.height),
      })
    );

    setUploading(true);
    setUploadProgress(0);

    const response = await axios.post(API_ENDPOINTS.UPLOAD_IMAGE, uploadFormData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      },
    });

    setUploading(false);
    setUploadProgress(0);
    return response.data;
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!isAllowedImageFile(file)) {
      alert('Invalid file type. Please upload JPG, JPEG, or PNG');
      return;
    }
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      alert(`File too large. ${maxImageUploadLabel()}`);
      return;
    }

    if (type === 'album') {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);
      uploadFormData.append('type', 'gallery');
      setUploading(true);
      try {
        const response = await axios.post(API_ENDPOINTS.UPLOAD_IMAGE, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (response.data.success) {
          setAlbumForm({ ...albumForm, cover_image: response.data.url });
          alert('Image uploaded successfully!');
        } else {
          alert(response.data.message);
        }
      } catch (error) {
        alert('Error uploading image');
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
      return;
    }

    pendingAlbumIdRef.current = photoForm.album_id || selectedAlbum?.id;
    setPendingUploadFile(file);
    setCropPreviewUrl(URL.createObjectURL(file));
    setShowPhotoForm(false);
  };

  const handleCropConfirm = async ({ croppedAreaPixels, showInHero }) => {
    try {
      const previewSrc = cropPreviewUrl;
      const dims = await getImageDimensions(previewSrc);
      const focal = focalPointFromCrop(croppedAreaPixels, dims.width, dims.height);
      const cropPayload = {
        x: Math.round(croppedAreaPixels.x),
        y: Math.round(croppedAreaPixels.y),
        width: Math.round(croppedAreaPixels.width),
        height: Math.round(croppedAreaPixels.height),
      };

      if (editingHeroPhoto) {
        const sourceUrl = editingHeroPhoto.original_image_url || editingHeroPhoto.image_url;
        const fullUrl = getImageUrl(sourceUrl);
        const blob = await fetch(fullUrl).then((r) => r.blob());
        const file = new File([blob], 'hero-recrop.jpg', { type: blob.type || 'image/jpeg' });
        const uploadResult = await uploadImageWithCrop(file, croppedAreaPixels);
        if (!uploadResult.success) {
          alert(uploadResult.message || 'Upload failed');
          return;
        }
        await axios.put(API_ENDPOINTS.ADMIN_GALLERY, {
          photo: {
            id: editingHeroPhoto.id,
            image_url: uploadResult.url,
            original_image_url: uploadResult.original_url || sourceUrl,
            crop_data: cropPayload,
            focal_point_x: focal.x,
            focal_point_y: focal.y,
            show_in_hero: showInHero ? 1 : 0,
            caption: editingHeroPhoto.caption,
          },
        });
        await fetchAlbums();
        closeCropModal();
        alert('Hero crop updated!');
        return;
      }

      if (!pendingUploadFile) return;
      const uploadResult = await uploadImageWithCrop(pendingUploadFile, croppedAreaPixels);
      if (!uploadResult.success) {
        alert(uploadResult.message || 'Upload failed');
        return;
      }

      await axios.post(API_ENDPOINTS.ADMIN_GALLERY, {
        photo: {
          album_id: pendingAlbumIdRef.current,
          image_url: uploadResult.url,
          original_image_url: uploadResult.original_url || null,
          crop_data: cropPayload,
          focal_point_x: focal.x,
          focal_point_y: focal.y,
          show_in_hero: showInHero ? 1 : 0,
          caption: photoForm.caption || '',
        },
      });

      await fetchAlbums();
      setPhotoForm({ album_id: '', image_url: '', caption: '' });
      closeCropModal();
      alert('Photo added with hero crop!');
    } catch (error) {
      console.error('Crop save error:', error);
      const apiMsg = error.response?.data?.message;
      alert(
        apiMsg ||
          error.message ||
          'Error saving cropped image. Run migration 002_photos_hero_crop.sql if columns are missing.'
      );
    }
  };

  const handleSaveAlbum = async () => {
    if (!albumForm.name) { 
      alert('Please enter an album name'); 
      return; 
    }
    try {
      if (editingAlbum) {
        await axios.put(API_ENDPOINTS.ADMIN_GALLERY, { album: { ...albumForm, id: editingAlbum } });
      } else {
        await axios.post(API_ENDPOINTS.ADMIN_GALLERY, { album: albumForm });
      }
      await fetchAlbums();
      setShowAlbumForm(false);
      setEditingAlbum(null);
      setAlbumForm({ name: '', description: '', cover_image: '' });
    } catch (error) { 
      alert('Error saving album'); 
    }
  };


  const handleDeleteAlbum = async (id) => {
    if (window.confirm('Are you sure you want to delete this album and all its photos?')) {
      try {
        await axios.delete(API_ENDPOINTS.ADMIN_GALLERY, { data: { album_id: id } });
        await fetchAlbums();
        if (selectedAlbum?.id === id) setSelectedAlbum(null);
      } catch (error) { 
        alert('Error deleting album'); 
      }
    }
  };

  const handleDeletePhoto = async (id, albumId) => {
    if (window.confirm('Are you sure you want to delete this photo?')) {
      try {
        await axios.delete(API_ENDPOINTS.ADMIN_GALLERY, { data: { photo_id: id } });
        await fetchAlbums(); // Refresh immediately
      } catch (error) { 
        alert('Error deleting photo'); 
      }
    }
  };

  const refreshGallery = async () => {
    setRefreshing(true);
    await fetchAlbums();
    setRefreshing(false);
  };

  if (loading) return <div className="loading">Loading gallery...</div>;

  return (
    <div className="gallery-management-modern">
      {/* Header Section */}
      <div className="gallery-header">
        <div className="header-left">
          <h1>Gallery Management</h1>
          <p className="subtitle">Masonry preview matches the public gallery — crop, hero badges, and fullscreen view included</p>
        </div>
        <div className="header-right">
          <button className="btn-icon" onClick={refreshGallery} disabled={refreshing}>
            <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
          </button>
          <button className="btn-primary" onClick={() => { setEditingAlbum(null); setAlbumForm({ name: '', description: '', cover_image: '' }); setShowAlbumForm(true); }}>
            <FolderPlus size={18} /> New Album
          </button>
        </div>
      </div>

      <div className="gallery-layout">
        {/* Albums Sidebar */}
        <div className="albums-sidebar">
          <div className="albums-list">
            {albums.map(album => (
              <motion.div
                key={album.id}
                className={`album-item ${selectedAlbum?.id === album.id ? 'active' : ''}`}
                whileHover={{ x: 5 }}
                onClick={() => setSelectedAlbum(album)}
              >
                <div className="album-cover">
                  {album.cover_image ? (
                    <img src={getImageUrl(album.cover_image)} alt={album.name} />
                  ) : (
                    <div className="album-cover-placeholder"><Image size={24} /></div>
                  )}
                </div>
                <div className="album-info">
                  <h4>{album.name}</h4>
                  <span>{album.photos?.length || 0} photos</span>
                </div>
                <div className="album-actions">
                  <button onClick={(e) => { e.stopPropagation(); setEditingAlbum(album.id); setAlbumForm({ name: album.name, description: album.description || '', cover_image: album.cover_image || '' }); setShowAlbumForm(true); }}>
                    <Edit size={16} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteAlbum(album.id); }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="gallery-main">
          {selectedAlbum ? (
            <>
              {/* Album Header */}
              <div className="album-header-modern">
                <div className="album-title-section">
                  <h2>{selectedAlbum.name}</h2>
                  {selectedAlbum.description && <p>{selectedAlbum.description}</p>}
                </div>
                <div className="album-stats">
                  <span className="stat-badge">
                    <Image size={16} /> {selectedAlbum.photos?.length || 0} Photos
                  </span>
                  <button className="btn-outline" onClick={() => { setPhotoForm({ ...photoForm, album_id: selectedAlbum.id }); setShowPhotoForm(true); }}>
                    <ImagePlus size={16} /> Add Photos
                  </button>
                </div>
              </div>

              <AdminPhotoMasonry
                photos={selectedAlbum.photos || []}
                albumName={selectedAlbum.name}
                onAddPhotos={() => {
                  setPhotoForm({ ...photoForm, album_id: selectedAlbum.id });
                  setShowPhotoForm(true);
                }}
                onCropHero={(photo) => {
                  setEditingHeroPhoto(photo);
                  setCropPreviewUrl(getImageUrl(photo.original_image_url || photo.image_url));
                }}
                onDelete={(photo) => handleDeletePhoto(photo.id, selectedAlbum.id)}
              />
            </>
          ) : (
            <div className="empty-state-modern">
              <FolderPlus size={48} />
              <h3>Select an album</h3>
              <p>Choose an album from the sidebar or create a new one to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Album Modal */}
      <AnimatePresence>
        {showAlbumForm && (
          <motion.div className="modal-overlay-modern" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowAlbumForm(false)}>
            <motion.div className="modal-content-modern" initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-modern">
                <h2>{editingAlbum ? 'Edit Album' : 'Create New Album'}</h2>
                <button className="modal-close-modern" onClick={() => setShowAlbumForm(false)}>×</button>
              </div>
              <div className="modal-body-modern">
                <div className="form-group-modern">
                  <label>Album Name *</label>
                  <input type="text" value={albumForm.name} onChange={(e) => setAlbumForm({...albumForm, name: e.target.value})} placeholder="e.g., Ceremony, Reception, Pre-wedding" />
                </div>
                <div className="form-group-modern">
                  <label>Description</label>
                  <textarea value={albumForm.description} onChange={(e) => setAlbumForm({...albumForm, description: e.target.value})} rows="3" placeholder="A brief description of this album" />
                </div>
                <div className="form-group-modern">
                  <label>Cover Image</label>
                  <div className="image-upload-modern">
                    {albumForm.cover_image && (
                      <div className="upload-preview">
                        <img src={getImageUrl(albumForm.cover_image)} alt="Cover preview" />
                        <button className="remove-preview" onClick={() => setAlbumForm({...albumForm, cover_image: ''})}>×</button>
                      </div>
                    )}
                    <div className="upload-area-modern">
                      <input 
                        ref={fileInputRef}
                        type="file" 
                        id="album-cover" 
                        accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png" 
                        onChange={(e) => handleImageUpload(e, 'album')} 
                        disabled={uploading} 
                        style={{ display: 'none' }} 
                      />
                      <button className="upload-btn-modern" onClick={() => document.getElementById('album-cover').click()} disabled={uploading}>
                        {uploading ? (
                          <>
                            <Loader2 size={18} className="spin" /> Uploading {uploadProgress}%
                          </>
                        ) : (
                          <>
                            <Upload size={18} /> {albumForm.cover_image ? 'Change Cover' : 'Choose Cover Image'}
                          </>
                        )}
                      </button>
                      <small>{maxImageUploadLabel()}</small>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer-modern">
                <button className="btn-secondary" onClick={() => setShowAlbumForm(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleSaveAlbum}>{editingAlbum ? 'Update' : 'Create'} Album</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Photo Modal */}
      <AnimatePresence>
        {showPhotoForm && (
          <motion.div className="modal-overlay-modern" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowPhotoForm(false)}>
            <motion.div className="modal-content-modern" initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-modern">
                <h2>Add Photo to {albums.find(a => a.id === photoForm.album_id)?.name}</h2>
                <button className="modal-close-modern" onClick={() => setShowPhotoForm(false)}>×</button>
              </div>
              <div className="modal-body-modern">
                <div className="form-group-modern">
                  <label>Photo Image *</label>
                  <p className="form-hint">After you choose a file, you can crop it for the homepage hero banner (16:9).</p>
                  <div className="image-upload-modern">
                    <div className="upload-area-modern">
                      <input 
                        ref={fileInputRef}
                        type="file" 
                        id="photo-upload" 
                        accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png" 
                        onChange={(e) => handleImageUpload(e, 'photo')} 
                        disabled={uploading} 
                        style={{ display: 'none' }} 
                      />
                      <button className="upload-btn-modern" onClick={() => document.getElementById('photo-upload').click()} disabled={uploading}>
                        {uploading ? (
                          <>
                            <Loader2 size={18} className="spin" /> Uploading {uploadProgress}%
                          </>
                        ) : (
                          <>
                            <Crop size={18} /> Choose Photo &amp; Crop
                          </>
                        )}
                      </button>
                      <small>{maxImageUploadLabel()}</small>
                    </div>
                  </div>
                </div>
                <div className="form-group-modern">
                  <label>Caption (Optional)</label>
                  <input type="text" value={photoForm.caption} onChange={(e) => setPhotoForm({...photoForm, caption: e.target.value})} placeholder="A beautiful moment..." />
                </div>
              </div>
              <div className="modal-footer-modern">
                <button className="btn-secondary" onClick={() => setShowPhotoForm(false)}>Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero crop modal */}
      <AnimatePresence>
        {cropPreviewUrl && (
          <ImageCropModal
            imageSrc={cropPreviewUrl}
            title={editingHeroPhoto ? 'Adjust hero crop' : 'Crop photo for hero section'}
            showInHero={editingHeroPhoto ? Number(editingHeroPhoto.show_in_hero) === 1 : true}
            onConfirm={handleCropConfirm}
            onCancel={closeCropModal}
          />
        )}
      </AnimatePresence>

    </div>
  );
};

export default GalleryManagement;